import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ApiKey } from '@/types';

interface ApiKeyRowProps {
  apiKey: ApiKey;
  onRevoke: (keyId: string) => void;
  isRevoking: boolean;
}

/**
 * Mask an API key for display
 * Shows: mm_xxxx...xxxx (first 4 and last 4 chars after prefix)
 */
function maskApiKey(): string {
  // We don't have the raw key, so just show a placeholder
  return `mm_****...****`;
}

/**
 * Format a date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '从未';
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ApiKeyRow({ apiKey, onRevoke, isRevoking }: ApiKeyRowProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleRevokeClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmRevoke = () => {
    onRevoke(apiKey.id);
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" fontWeight={600}>
              {apiKey.name || '未命名 Key'}
            </Typography>
            <Chip
              label={maskApiKey()}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
            <Typography variant="caption">
              创建时间: {formatDate(apiKey.created_at)}
            </Typography>
            <Typography variant="caption">
              上次使用: {formatDate(apiKey.last_used_at)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="删除此 API Key">
            <IconButton
              color="error"
              onClick={handleRevokeClick}
              disabled={isRevoking}
              size="small"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Confirm Revoke Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>删除 API Key？</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除 "{apiKey.name || '未命名 Key'}" 吗？
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            此操作无法撤销。使用此 Key 的应用将无法继续工作。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>取消</Button>
          <Button onClick={handleConfirmRevoke} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
